import { LitElement, html } from 'lit';
import { styles } from './css';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { logErrorToSentry } from '../../helpers/helpers';
import { imagebossUrl } from '../../helpers/imageboss';
import '../../lib/lit-3form-block/components/cta-button';
import {
  getTokenFromMetaTag as getCSRFToken,
  HEADER_PARAM as CSRF_HEADER_PARAM,
} from '@Helpers/csrf';

import { Core as Uppy, AwsS3 } from 'uppy';

import { fireAdobeAnalyticsEvent } from '../../helpers/adobe-analytics';

export class QuoteRequests extends LitElement {
  static get styles() {
    return [styles];
  }

  static properties = {
    _submitSuccess: { type: Boolean },
    _submitButtonDisabled: { type: Boolean },
    _formFields: { type: Array },
    _fileList: { type: Array },
    _showValidationPrompt: { type: Boolean },
    _referenceImageUrl: { type: String },
    _referenceImageDescription: { type: String },
    usedLocalStorage: { type: Boolean },
    completedFirstUpdated: { type: Boolean },
    reportedFormStart: { type: Boolean },
  };

  constructor() {
    super();

    this._formFields = this._formFieldsData;
    this._fileList = [];
    this._submitSuccess = false;
    this._submitButtonDisabled = false;
    this._showValidationPrompt = false;
    this._referenceImageUrl = '';
    this._referenceImageDescription = '';
    this.usedLocalStorage = false;
    this.completedFirstUpdated = false;
    this.reportedFormStart = false;

    this.addEventListener('files-added', e => {
      this._fileList.push(e.detail.data);
      this.requestUpdate();
    });
  }

  firstUpdated() {
    // Create new configured Uppy instance along with it's event listeners
    this.configureUppy();
    this.populateFields();

    // If a rendered image is provided it needs to be included in the uploads
    // outside of the current processing queue
    setTimeout(() => {
      if (this.usedLocalStorage) {
        this.addReferenceImageToUppy();
      }
      this.completedFirstUpdated = true;
    }, 50);
  }

  render() {
    return html`
      <div id="quote-request-wrapper" class="page-wrapper">
        <div class="header">
          <h1>Request a Quote</h1>
          <h4>
            ${this._subheaderStr}
          </h4>
        </div>
        <div class="form-wrapper">
          ${!this._submitSuccess
            ? html`
                <form id="request-quote-form" autocomplete="on">
                  ${this._formFields.map(
                    row => html`
                      <div class="input-row">
                        ${row.map(field => {
                          if (!field.elementType) {
                            return html`
                              <span
                                class="input-wrapper"
                                id="${field.id}-wrapper"
                                style="${styleMap(this.validBorder(field))}"
                              >
                                <input
                                  .type=${field.inputType}
                                  .id=${field.id}
                                  .name=${field.id}
                                  .value=${field.value}
                                  @change="${this.checkFieldValid}"
                                  .autocomplete=${field.autocomplete}
                                />
                                <label
                                  for=${field.id}
                                  class="${classMap(
                                    this.validityClasses(field)
                                  )}"
                                  >${field.placeholder}</label
                                >
                              </span>
                            `;
                          } else {
                            return html`
                              <span
                                class="input-wrapper"
                                id="${field.id}-wrapper"
                                style="${styleMap(this.validBorder(field))}"
                              >
                                <textarea
                                  .id=${field.id}
                                  .name=${field.id}
                                  .value=${field.value}
                                  @change="${this.checkFieldValid}"
                                ></textarea>
                                <label
                                  for=${field.id}
                                  class="${classMap(
                                    this.validityClasses(field)
                                  )}"
                                  >${field.placeholder}</label
                                >
                              </span>
                            `;
                          }
                        })}
                      </div>
                    `
                  )}
                  <div id="uploads-wrapper">
                    <input
                      type="file"
                      id="request-quote-documents"
                      name="request-quote[images][]"
                      multiple
                      hidden
                    />
                    <label for="request-quote-documents" class="upload-trigger">
                      <img src="/static/icons/SVG/upload-black-12x16.svg" />
                      <p>Upload supporting documents</p>
                    </label>
                    ${this._fileList.length > 0
                      ? html`
                          <div id="selectedFiles">
                            ${this._fileList.map(
                              (file, index) => html`
                                <span>
                                  <p>${file.metadata.filename}</p>
                                  <p
                                    class="remove-file"
                                    @click="${this.removeFile}"
                                    fileIndex="${index}"
                                    fileId="${file.id}"
                                  >
                                    remove
                                  </p>
                                </span>
                              `
                            )}
                          </div>
                        `
                      : html``}
                    <p class="fine-print">
                      File uploads are capped at 500MB per file. For files
                      larger than 500MB, please compress them prior to
                      uploading. If you experience any issues, submit this form
                      and then email the files directly to
                      <a href="mailto:rfq@3-form.com">rfq@3-form.com</a>.
                    </p>
                  </div>
                  ${this._showValidationPrompt
                    ? html`
                        <p class="validation-prompt">
                          Error: Required field is missing.
                        </p>
                      `
                    : html``}
                  <cta-button
                    class="cta-btn"
                    cta-data=${this.ctaData('Submit')}
                    cta-type="primary"
                    @click="${this.trySubmit}"
                    ?disabled="${this._submitButtonDisabled}"
                  ></cta-button>
                </form>
              `
            : html`
                <div class="success-wrapper">
                  <cta-button
                    class="success-cta-btn"
                    cta-data=${this.ctaData('Home')}
                    cta-type="primary"
                    @click="${this.goHome}"
                  ></cta-button>
                  <cta-button
                    class="success-cta-btn"
                    cta-data=${this.ctaData('Quote Another Project')}
                    cta-type="secondary"
                    @click="${this.quoteAnotherProject}"
                  ></cta-button>
                </div>
              `}
          ${this._referenceImageUrl
            ? html`
                <div class="reference-img-wrapper">
                  <img src="${this._referenceImageUrl}" />
                  ${this._referenceImageDescription
                    ? html`
                        <p class="description">
                          ${unsafeHTML(
                            this.withLineBreaks(this._referenceImageDescription)
                          )}
                        </p>
                      `
                    : html``}
                </div>
              `
            : html``}
        </div>
      </div>
    `;
  }

  populateFields() {
    // EXPECTED DATA STRUCTURE
    // previousPageData: {
    //   description: 'string with line breaks',
    //   image: {
    //     url: 'a url',
    //     description: 'string with line breaks'
    //   }
    // }

    let previousPageData = JSON.parse(
      window.sessionStorage.getItem('requestInfoData')
    );

    // This is for the bitreel application and is a temporary work around until
    // the My3form repo is ready for us to push directly to the lead dashboard.
    // Hopefully this is no longer needed after June 2025... but we'll see.
    if (!previousPageData) {
      const localStorageRaw = localStorage.getItem('requestInfoData');

      if (localStorageRaw) {
        this.usedLocalStorage = true;
        try {
          previousPageData = JSON.parse(localStorageRaw);
          const nonRequiredFields = [
            'phone',
            'address1',
            'city',
            'state',
            'install_location',
          ];
          this._formFields = this._formFields.map(row =>
            row.map(field => {
              if (nonRequiredFields.includes(field.id)) {
                return {
                  ...field,
                  valid: true,
                  placeholder: field.placeholder.includes('(optional)')
                    ? field.placeholder
                    : `${field.placeholder} (optional)`,
                };
              }
              return field;
            })
          );
        } catch (e) {
          console.log('Invalid JSON in localStorage requestInfoData:', e);
        }
      }
    }

    if (previousPageData) {
      // Update description text field
      if (previousPageData.description) {
        const descriptionField = this.shadowRoot.getElementById('description');
        descriptionField.value = this.withCarriageReturns(
          previousPageData.description
        );
        const event = new Event('change');
        descriptionField.dispatchEvent(event);
      }

      // Update Image data
      if (previousPageData.image) {
        this._referenceImageUrl = !!previousPageData.image?.imgObj
          ? previousPageData.image.imgObj
          : previousPageData.image.url;
        this._referenceImageDescription = previousPageData.image.description;
      }
    } else {
      this._referenceImageUrl = '';
      this._referenceImageDescription = '';
    }
    this.requestUpdate();
  }

  withLineBreaks(string) {
    return !string ? '' : string.split(/\r\n|\n/g).join('<br>');
  }

  withCarriageReturns(string) {
    return !string
      ? ''
      : string
          .replace(/<\/?b>/g, '')
          .split(/<br>/g)
          .join('\n');
  }

  // Handle individual field validations

  checkFieldValid(event) {
    const location = this.inputFieldLocation(event.target.id);
    const fieldObject = this._formFields[location[0]][location[1]];

    fieldObject.value = event.target.value;
    fieldObject.valid = this.updateFieldValidity(fieldObject);
    fieldObject.touched = true;

    if (this._formIsValid) {
      this._showValidationPrompt = false;
    }

    if (this.completedFirstUpdated && !this.reportedFormStart) {
      this.sendAnalyticsFormStart();
      this.reportedFormStart = true;
    }

    this.requestUpdate();
  }

  validityClasses(field) {
    return {
      valid: field.valid,
      invalid: !field.valid,
      touched: field.touched,
      'input-label': true,
    };
  }

  validBorder(field) {
    const valid = { 'border-color': '#cccccc' };
    const invalid = { 'border-color': '#ff0000' };

    if (field.valid) return valid;

    return !field.touched
      ? valid
      : field.value && field.touched
      ? valid
      : invalid;
  }

  updateFieldValidity(fieldObject) {
    if (fieldObject.id === 'address2') {
      return true;
    }
    switch (fieldObject.inputType) {
      case 'text':
        return !!fieldObject.value;
      case 'email':
        return /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(
          fieldObject.value
        );
      case 'tel':
        return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(
          fieldObject.value
        );
      case 'number':
        switch (fieldObject.id) {
          case 'zip':
          case 'install_location':
            return fieldObject.value.length >= 5;
          default:
            break;
        }
      default:
        break;
    }
  }

  inputFieldLocation(id) {
    let location;
    this._formFields.forEach((fieldRow, ii) => {
      fieldRow.forEach((field, jj) => {
        if (field.id === id) {
          location = [ii, jj];
        }
      });
    });
    return location;
  }

  trySubmit() {
    if (!this._formIsValid) {
      this.touchEachField();
      this._showValidationPrompt = true;
      this.requestUpdate();
    } else {
      this.submitFormData(this._formDataObject);
    }
  }

  submitFormData(formData) {
    if (this._fileList.length > 0) {
      const fileList = this._fileList.map(file => {
        return {
          file: file,
        };
      });
      formData['quote_request_files_attributes'] = fileList;
    }

    const headers = { 'Content-Type': 'application/json' },
      data = {
        method: 'POST',
        headers,
        body: JSON.stringify({ quote_request: formData }),
      };

    headers[CSRF_HEADER_PARAM] = getCSRFToken();

    this._submitButtonDisabled = true;
    this.requestUpdate();

    fetch('/request-quote', data)
      .then(response => {
        this._submitButtonDisabled = false;
        if (response.ok) {
          this._submitSuccess = true;
          window.scrollTo({ top: 0 });
          this.sendAnalyticsEvent();
        }
        this.requestUpdate();
        return response.ok;
      })
      .then(json => {})
      .catch(error => {
        logErrorToSentry(error);
      });

    this.sendAnalyticsRequestQuoteSent();
  }

  removeFile(e) {
    const index = parseInt(e.target.getAttribute('fileIndex'));
    const fileId = e.target.getAttribute('fileId');

    if (!!fileId) {
      uppy.removeFile(fileId);
    }
    this._fileList.splice(index, 1);

    this.requestUpdate();
  }

  touchEachField() {
    this._formFields.forEach(row => {
      row.forEach(field => (field.touched = true));
    });
  }

  ctaData(text) {
    return JSON.stringify({
      text: text,
      fullWidth: true,
    });
  }

  goHome() {
    window.location = '/';
  }

  sendAnalyticsEvent() {
    if (typeof gtag !== 'function') return;

    gtag('event', 'Request Quote', {
      event_category: 'Quote Request',
    });
  }

  quoteAnotherProject() {
    window.location.reload();
  }

  configureUppy() {
    const fileInput = this.shadowRoot.querySelector('#request-quote-documents');
    this.uppy = new Uppy({
      debug: true,
      autoProceed: true,
      restrictions: {
        maxFileSize: 524288000, //500Mb
        maxNumberOfFiles: 10,
        minNumberOfFiles: 1,
      },
    });

    const uppy = this.uppy;
    uppy.use(AwsS3, {
      companionUrl: '/',
    });

    fileInput.addEventListener('change', event => {
      const files = Array.from(event.target.files);
      this._submitButtonDisabled = true;
      this.requestUpdate();

      files.forEach(file => {
        try {
          uppy.addFile({
            source: 'file input',
            name: file.name,
            type: file.type,
            data: file,
          });
        } catch (error) {
          this._submitButtonDisabled = false;
          this.requestUpdate();
          logErrorToSentry(error);
        }
      });
    });

    // it’s probably a good idea to clear the `<input>`
    // after the upload or when the file was removed
    // (see https://github.com/transloadit/uppy/issues/2640#issuecomment-731034781)
    uppy.on('file-removed', () => {
      this._submitButtonDisabled = false;
      fileInput.value = null;
    });

    uppy.on('complete', () => {
      this._submitButtonDisabled = false;
      fileInput.value = null;
    });

    uppy.on('upload-success', file => {
      this._submitButtonDisabled = false;
      uppy.setState({
        allowNewUpload: true,
      });

      // construct uploaded file data in the format that Shrine expects
      var uploadedFileData = {
        id: file.meta['key'].match(/^cache\/(.+)/)[1], // object key without prefix
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      };

      let filesAdded = new CustomEvent('files-added', {
        detail: {
          message: 'files-uploaded happened.',
          data: uploadedFileData,
        },
        bubbles: true,
        composed: true,
      });
      this.dispatchEvent(filesAdded);
    });
  }

  addReferenceImageToUppy() {
    const uppy = this.uppy;

    // Convert _referenceImageUrl base64 image to binary, but don't include
    // the headers
    const imageAsBase64 = this._referenceImageUrl.split(',')[1];
    const imageAsBinary = atob(imageAsBase64);
    const bytes = new Uint8Array(
      Array.from(imageAsBinary, char => char.charCodeAt(0))
    );
    const file = new File([bytes], 'web-rendered-image.png', {
      type: 'image/png',
    });

    try {
      this.uppy.addFile({
        source: 'base64',
        name: file.name,
        type: file.type,
        data: file,
      });
    } catch (error) {
      logErrorToSentry(error);
    }
  }

  sendAnalyticsFormStart() {
    const eventData = {
      type: 'form:start',
      detail: {
        eVar40: 'Contact|Request a Quote',
      },
    };
    const target = this.shadowRoot.querySelector('#request-quote-form');
    fireAdobeAnalyticsEvent(eventData, target);
  }

  sendAnalyticsRequestQuoteSent() {
    const eventData = {
      type: 'form:submit',
      detail: {
        eVar40: 'Contact|Request a Quote',
      },
    };
    const target = this.shadowRoot.querySelector('quote-request-wrapper');
    fireAdobeAnalyticsEvent(eventData, target);
  }

  get _subheaderStr() {
    return this._submitSuccess
      ? 'Thank you for your request. A representative from 3form will contact you within 24 hours.'
      : 'Please provide the information below and we will contact you shortly.';
  }

  get _formIsValid() {
    return this._flattenedFormFields.filter(field => !field.valid).length === 0;
  }

  get _formDataObject() {
    const formDataObject = {};
    this._flattenedFormFields.map(field => {
      formDataObject[field.id] = field.value;
    });
    return formDataObject;
  }

  get _flattenedFormFields() {
    return this._formFields.flat();
  }

  get _formFieldsData() {
    return [
      [
        {
          id: 'first_name',
          placeholder: 'First Name',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: 'given-name',
        },
        {
          id: 'last_name',
          placeholder: 'Last Name',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: 'family-name',
        },
      ],
      [
        {
          id: 'email',
          placeholder: 'Email',
          inputType: 'email',
          value: '',
          valid: false,
          autocomplete: 'email',
        },
        {
          id: 'phone',
          placeholder: 'Phone',
          inputType: 'tel',
          value: '',
          valid: false,
          autocomplete: 'tel-national',
        },
      ],
      [
        {
          id: 'company_name',
          placeholder: 'Company Name',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
        {
          id: 'country',
          placeholder: 'Country',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: 'country',
        },
      ],
      [
        {
          id: 'address1',
          placeholder: 'Address Line 1',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: 'street-address',
        },
        {
          id: 'address2',
          placeholder: 'Address Line 2 (optional)',
          inputType: 'text',
          value: '',
          valid: true,
          autocomplete: '',
        },
      ],
      [
        {
          id: 'city',
          placeholder: 'City',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
        {
          id: 'state',
          placeholder: 'State/Prov/Region',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
        {
          id: 'zip',
          placeholder: 'Zip Code',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: 'postal-code',
        },
      ],
      [
        {
          id: 'project_name',
          placeholder: 'Project Name (As shown on title block)',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
      ],
      [
        {
          id: 'specifying_designer_company',
          placeholder: 'Specifying Designer Company',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
        {
          id: 'install_location',
          placeholder: 'Project Installation Location (Zip Code)',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
        },
      ],
      [
        {
          id: 'description',
          placeholder: 'Description of Materials and Quantities Needed',
          inputType: 'text',
          value: '',
          valid: false,
          autocomplete: '',
          elementType: 'textarea',
        },
      ],
    ];
  }
}

customElements.define('quote-requests', QuoteRequests);
